import React, { useRef, useEffect, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';

interface Moon3DProps {
  position?: [number, number, number];
  scale?: number;
  rotationSpeed?: number;
}

const Moon3D: React.FC<Moon3DProps> = ({
  position = [0, 80, -200],
  scale = 15,
  rotationSpeed = 0.001
}) => {
  const moonRef = useRef<THREE.Group>(null);
  const moonTexture = useRef<THREE.Texture | null>(null);
  const moonBumpMap = useRef<THREE.Texture | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error] = useState<string | null>(null);

  // Ordinal inscription details
  const inscriptionId = '13130e4b299ed361f2a734f6433844ef0f0211cd504e0ca8f4d4ab20f51b8127i0';
  const inscriptionNumber = '76545890';

  // Em um ambiente real, você buscaria o modelo 3D diretamente da inscrição Bitcoin Ordinal
  // Aqui estamos usando um modelo de exemplo para demonstração

  useEffect(() => {
    // URL para acessar o conteúdo da inscrição Bitcoin Ordinal
    const ordinalContentUrl = `https://ordinals.com/content/${inscriptionId}`;

    // Para demonstração, usamos uma esfera simples para representar a lua
    // Em uma implementação real, você buscaria o modelo 3D da inscrição
    console.log(`Carregando modelo da lua da inscrição Bitcoin Ordinal #${inscriptionNumber}`);
    console.log(`URL do conteúdo da inscrição: ${ordinalContentUrl}`);
    console.log(`Usando esfera simples para representar a lua`);

    // Carregar texturas manualmente
    const textureLoader = new THREE.TextureLoader();
    const textureUrl = 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/textures/planets/moon_1024.jpg';

    textureLoader.load(
      textureUrl,
      (texture) => {
        moonTexture.current = texture;

        // Carregar o bump map após a textura principal
        textureLoader.load(
          textureUrl, // Mesmo URL para bump map neste exemplo
          (bumpTexture) => {
            moonBumpMap.current = bumpTexture;
            setIsLoading(false);
          },
          undefined,
          (error) => console.error('Erro ao carregar bump map:', error)
        );
      },
      undefined,
      (error) => console.error('Erro ao carregar textura:', error)
    );

    // Cleanup
    return () => {
      if (moonTexture.current) {
        moonTexture.current.dispose();
      }
      if (moonBumpMap.current) {
        moonBumpMap.current.dispose();
      }
    };
  }, [inscriptionId, inscriptionNumber]);

  // Não precisamos mais carregar um modelo 3D externo

  // Animate the moon rotation
  useFrame(() => {
    if (moonRef.current) {
      moonRef.current.rotation.y += rotationSpeed;
    }
  });

  if (isLoading) {
    return null;
  }

  if (error) {
    console.error('Error loading moon model:', error);
    return null;
  }

  // Refs já declarados no início do componente

  return (
    <group ref={moonRef} position={new THREE.Vector3(...position)} scale={[scale, scale, scale]}>
      <mesh>
        <sphereGeometry args={[1, 64, 64]} />
        <meshStandardMaterial
          map={moonTexture.current}
          bumpMap={moonBumpMap.current}
          color="#E1E1E1"
          emissive="#333333"
          roughness={0.7}
          metalness={0.15}
          bumpScale={0.03}
        />
        {/* Adicionar um ponto de luz para iluminar a lua */}
        <pointLight
          position={[10, 10, 10]}
          intensity={0.8}
          color="#FFFFFF"
          distance={100}
        />
      </mesh>
      {error && (
        <Html position={[0, 1.5, 0]}>
          <div style={{ color: 'red', background: 'rgba(0,0,0,0.7)', padding: '10px' }}>
            Erro: {error}
          </div>
        </Html>
      )}
    </group>
  );
};

export default Moon3D;
